import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Eye, FileDown, FileText, ShieldAlert, ShieldOff, ShieldCheck, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useReportDownload } from "@/lib/ReportDownloadProvider";

function fmtTime(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}m ${sec}s`;
}

export default function AdminEmployeeResult() {
  const { id } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [lockHistory, setLockHistory] = useState(null);
  const { start: startDownload, busy: busyExport, job } = useReportDownload();
  useEffect(() => {
    (async () => {
      const r = await api.get(`/admin/employees/${id}/result`);
      setData(r.data);
    })();
    (async () => {
      try {
        const r = await api.get(`/admin/employees/${id}/lock-history`);
        setLockHistory(r.data);
      } catch (e) { /* silent */ }
    })();
  }, [id]);

  const _reportName = () => `${data?.employee?.first_name || ""} ${data?.employee?.last_name || ""}`.trim() || "Report";
  const _fileStem = () => {
    const raw = `${data?.employee?.first_name || "Candidate"}_${data?.employee?.last_name || ""}`.trim();
    return raw.replace(/\s+/g, "_").replace(/[^A-Za-z0-9_-]/g, "") || "Candidate";
  };
  const onFullPdf = () => {
    if (!data?.submissions?.length) { toast.error("No submissions to export"); return; }
    startDownload({
      url: `/admin/employees/${id}/full-report.pdf`,
      filename: `PelicoData_FullReport_${_fileStem()}.pdf`,
      label: _reportName(),
    });
  };
  const onFullCsv = () => {
    if (!data?.submissions?.length) { toast.error("No submissions to export"); return; }
    startDownload({
      url: `/admin/employees/${id}/full-report.csv`,
      filename: `PelicoData_FullReport_${_fileStem()}.csv`,
      label: _reportName(),
    });
  };

  const onLock = async () => {
    if (!data?.employee) return;
    const name = `${data.employee.first_name || ""} ${data.employee.last_name || ""}`.trim();
    if (!window.confirm(`Lock ${name || "this candidate"}'s account? They will no longer be able to log in or submit new pages until you unlock them.`)) return;
    try {
      await api.post(`/admin/employees/${id}/lock`);
      toast.success(`${name} · account locked`);
      // Refresh employee + lock history so the UI reflects the change
      const r = await api.get(`/admin/employees/${id}/result`);
      setData(r.data);
      const lh = await api.get(`/admin/employees/${id}/lock-history`);
      setLockHistory(lh.data);
    } catch (e) {
      toast.error("Failed to lock account");
    }
  };
  const onUnlock = async () => {
    if (!data?.employee) return;
    const name = `${data.employee.first_name || ""} ${data.employee.last_name || ""}`.trim();
    if (!window.confirm(`Unlock ${name || "this candidate"}'s account?`)) return;
    try {
      await api.post(`/admin/employees/${id}/unlock`);
      toast.success(`${name} · account unlocked`);
      const r = await api.get(`/admin/employees/${id}/result`);
      setData(r.data);
      const lh = await api.get(`/admin/employees/${id}/lock-history`);
      setLockHistory(lh.data);
    } catch (e) {
      toast.error("Failed to unlock account");
    }
  };

  if (!data) return <div className="text-zinc-500">{t("loading")}</div>;
  const e = data.employee;
  return (
    <div>
      <Link to="/admin/employees" className="text-sm text-zinc-500 inline-flex items-center gap-1 mb-4"><ArrowLeft size={14} /> {t("back")}</Link>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <h1 className="font-display text-4xl font-bold tracking-tight">{t("results")}</h1>
        <div className="flex gap-2 flex-wrap">
          <Link
            to={`/admin/employees/${id}/forensic`}
            data-testid="open-forensic"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ink)] text-white text-sm font-semibold hover:bg-[var(--accent)]"
          >
            <ShieldAlert size={14} /> Forensic Investigation
          </Link>
          <button
            data-testid="download-full-pdf"
            onClick={onFullPdf}
            disabled={busyExport || !data.submissions.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ink)] text-white text-sm font-semibold hover:bg-[var(--accent)] disabled:opacity-50"
          >
            <FileDown size={14} /> {busyExport && job?.kind === "pdf" ? `${job.done}/${job.total} · PDF…` : "Download Full Result (PDF)"}
          </button>
          <button
            data-testid="download-full-csv"
            onClick={onFullCsv}
            disabled={busyExport || !data.submissions.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line)] bg-white text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50"
          >
            <FileText size={14} /> CSV
          </button>
          <button
            data-testid="view-lock-history-btn"
            onClick={() => {
              const el = document.querySelector('[data-testid="admin-lock-history-table"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
              (lockHistory?.lock_count || 0) > 0
                ? "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
                : "bg-white border-[var(--line)] text-zinc-600 hover:bg-zinc-50"
            }`}
            title="View lock/unlock history"
          >
            <ShieldOff size={14} />
            Locked {lockHistory?.lock_count ?? 0}× · View History
          </button>
          {e.locked ? (
            <button
              data-testid="unlock-account-btn"
              onClick={onUnlock}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
              title="Unlock this candidate's account"
            >
              <ShieldCheck size={14} /> Unlock Account
            </button>
          ) : (
            <button
              data-testid="lock-account-btn"
              onClick={onLock}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-700 text-white text-sm font-semibold hover:bg-rose-800"
              title="Lock this candidate's account — they will not be able to log in or submit until unlocked"
            >
              <ShieldOff size={14} /> Lock Account
            </button>
          )}
        </div>
      </div>
      {e.locked && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-center gap-3">
          <ShieldOff size={18} className="text-rose-700 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-rose-800">Account is currently locked</div>
            <div className="text-rose-700 text-xs mt-0.5">
              Reason: <span className="font-mono">{e.lock_reason || "Locked by admin"}</span>
            </div>
          </div>
        </div>
      )}
      <div className="card-clean p-6 mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          [t("name"), `${e.first_name} ${e.last_name}`],
          [t("email"), e.email],
          ["Project", e.project_name || "Project 1"],
          [`${t("total")} ${t("pages")}`, e.total_pages],
          [t("completed"), e.completed_pages],
          [t("start_date"), e.start_date],
          [t("last_date"), e.last_date],
          [`${t("from_page")}–${t("to_page")}`, `${e.from_page} – ${e.to_page}`],
          ["Real Accuracy", `${data.total_accuracy}%`],
          ["Display Accuracy (Candidate view)", `${data.display_accuracy}%`],
          [`Avg ${t("wpm")}`, data.avg_wpm],
          [`${t("total")} ${t("mistakes")}`, data.total_mistakes],
          ["100% Accuracy Pages", data.total_pages_100_acc || 0],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="text-xs uppercase tracking-widest text-zinc-500">{k}</div>
            <div className="text-sm font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="card-clean overflow-x-auto mt-6">
        <table className="w-full table-clean">
          <thead className="bg-zinc-50">
            <tr>{[t("page"), t("accuracy"), t("wpm"), t("mistakes"), t("time_taken"), t("submitted"), t("actions")].map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.submissions.map((s) => (
              <tr key={s.id} className="border-t border-[var(--line)] hover:bg-zinc-50">
                <td className="px-4 py-3">{s.page_name}</td>
                <td className="px-4 py-3"><span className={`chip ${s.accuracy >= 95 ? "text-green-700 border-green-200" : s.accuracy >= 80 ? "text-amber-700 border-amber-200" : "text-red-700 border-red-200"}`}>{s.accuracy}%</span></td>
                <td className="px-4 py-3">{s.wpm}</td>
                <td className="px-4 py-3">{s.mistakes}</td>
                <td className="px-4 py-3 text-xs">{fmtTime(s.time_taken_seconds)}</td>
                <td className="px-4 py-3 text-xs">{new Date(s.submitted_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <Link to={`/admin/submissions/${s.id}`} data-testid={`view-sub-${s.id}`} className="p-1.5 rounded hover:bg-zinc-100 inline-flex items-center gap-1 text-xs"><Eye size={14} /> {t("view")}</Link>
                </td>
              </tr>
            ))}
            {!data.submissions.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">—</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Lock History audit trail */}
      <div className="card-clean mt-6 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[var(--line)] bg-zinc-50">
          <div className="flex items-center gap-2">
            <History size={16} className="text-rose-600" />
            <h2 className="font-display text-lg font-bold">Lock History</h2>
            {lockHistory && (
              <span className="text-xs text-zinc-500">
                {lockHistory.lock_count} lock{lockHistory.lock_count === 1 ? "" : "s"} · {lockHistory.unlock_count} unlock{lockHistory.unlock_count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {lockHistory?.employee?.locked ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-bold uppercase tracking-widest">
              <ShieldOff size={11} /> Currently Locked
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase tracking-widest">
              <ShieldCheck size={11} /> Active
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table data-testid="admin-lock-history-table" className="w-full table-clean">
            <thead className="bg-white">
              <tr>
                {["When", "Type", "Reason / Detail", "Page", "Session · IP", "Device", "Actor"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 whitespace-nowrap text-xs uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(lockHistory?.events || []).map((e) => {
                const isLock = e.event_type === "lock";
                return (
                  <tr key={e.id} className="border-t border-[var(--line)] hover:bg-zinc-50 align-top">
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{new Date(e.occurred_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`chip ${isLock ? "text-rose-700 border-rose-200" : "text-emerald-700 border-emerald-200"}`}>
                        {isLock ? "LOCK" : "UNLOCK"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm max-w-md">
                      {isLock ? (
                        <>
                          <div className="font-medium text-zinc-900">{e.reason_human || "Security policy violation."}</div>
                          {e.violation_type && <div className="text-xs text-zinc-500 mt-0.5">Type: <span className="font-mono">{e.violation_type}</span></div>}
                          {e.detail && <div className="text-xs text-zinc-500 mt-0.5">{e.detail}</div>}
                        </>
                      ) : (
                        <>
                          <div className="font-medium text-zinc-900">Account unlocked</div>
                          {e.previous_lock_reason && <div className="text-xs text-zinc-500 mt-0.5">Previous reason: {e.previous_lock_reason}</div>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">{e.page_number != null ? `Page ${e.page_number}` : "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-mono truncate max-w-[180px]">{e.session_id ? e.session_id.slice(0, 12) + "…" : "—"}</div>
                      <div className="text-zinc-500">{e.ip || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {e.device ? (
                        <div className="text-zinc-700">
                          {[e.device.browser, e.device.os, e.device.device_type].filter(Boolean).join(" · ") || "—"}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {isLock ? (
                        <span className="text-zinc-500">System</span>
                      ) : (
                        <span className="text-zinc-700">{e.admin_email || "Admin"}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(!lockHistory || !lockHistory.events?.length) && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500 text-sm">No lock or unlock events recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
